import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { type Dispatch, type SetStateAction, memo } from "react";
import type { ArticuloStock, Fase, PrintData } from "./GestionFases";
import { CSS } from "@dnd-kit/utilities";

interface RowProps {
	readonly orden: FaseWithID;
	readonly selection: string[];
	readonly setSelection: Dispatch<SetStateAction<string[]>>;
	readonly sorting: boolean;
	readonly print: PrintData | null;
	readonly vista: string;
	readonly stock: ArticuloStock | undefined;
}

export function SortableRow(props: RowProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
	} = useSortable({ id: props.orden.id, disabled: !props.sorting });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		// eslint-disable-next-line react/jsx-props-no-spreading
		<tr ref={setNodeRef} style={style} {...attributes}>
			{/*  eslint-disable-next-line react/jsx-props-no-spreading */}
			<RowContent {...props} listeners={listeners} />
		</tr>
	);
}

const RowContent = memo(({ orden, selection, setSelection, sorting, print, vista, stock, listeners }: RowProps & { readonly listeners: SyntheticListenerMap | undefined; }) => {
	const formatter = new Intl.DateTimeFormat("es-ES", { year: "numeric", month: "2-digit", day: "2-digit" });
	const formatDate = (timestamp: number | null | undefined) => timestamp == null ? "-" : formatter.format(new Date(timestamp * 1000));
	const numberFormat = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2, minimumFractionDigits: 0 });
	const formatNumber = (value: number | undefined | null) => value == null ? "-" : numberFormat.format(value);

	const toggleRow = (checked: boolean) => {
		if (checked) {
			setSelection([...selection, orden.id]);
		} else {
			setSelection((prev) => prev.filter((id) => id !== orden.id));
		}
	};

	return (
		<>
			{sorting ? <td
				className="dt-center noClick"
				style={{ textAlign: "center", touchAction: "none", cursor: "grab" }}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...listeners}>
				<i className="fa fa-arrows-alt" style={{ width: "100%" }} />
			</td> : (
				<td
					style={{ textAlign: "center" }}
					onClick={(event) => {
						const check = (event.target as HTMLElement).querySelector("input[type='checkbox']");
						if (check) {
							(check as HTMLElement).click();
						}
					}} >
					<input
						type="checkbox"
						checked={selection.includes(orden.id)}
						onChange={(event) => {
							toggleRow(event.currentTarget.checked);
						}}
					/>
				</td>
			)}
			<td style={{ fontWeight: "bold" }}>{orden.id}</td>
			{vista === "Fase" && <td>{orden.descripcionFase}</td>}
			<td>{orden.idArticulo} - {orden.descripcionArticulo}</td>
			<td>{orden.cantAcabada}/{orden.cantLanzada} ({orden.cantidadOF})</td>
			{vista === "Fase" ? <>
				<td>{formatDate(orden.timestampIni)}</td>
				<td>{formatDate(orden.timestampFin)}</td>
				<td>{formatDate(print?.last)}</td>
			</> : <>
				<td>{formatNumber(stock?.max)}</td>
				<td>{formatNumber(stock?.min)}</td>
				<td>{formatNumber(stock?.pendienteEntrada)}</td>
				<td>{formatNumber(stock?.pendienteSalida)}</td>
				<td>{formatNumber(stock?.stock)}</td>
				<td>{formatNumber(stock?.entrarRecomendado)}</td>
			</>}
		</>
	);
});
RowContent.displayName = "RowContent";

export type FaseWithID = Fase & { id: string; };